import React, { useState } from 'react';
import T from 'prop-types';

import PageLayout from './PageLayout';
import PressRegisterForm from '../components/PressRegisterForm';
import StateRequestSubmitted from '../components/StateRequestSubmitted';
import StateCheckInbox from '../components/StateCheckInbox';
import StateExpired from '../components/StateExpired';
import StateRenewalSubmitted from '../components/StateRenewalSubmitted';
import { registerPressUser, requestPressRenewal } from '../api';

const signInUrl = '/users/sign_in';

const fielPrefixes = [
  { prefix: 'First name', field: 'first_name' },
  { prefix: 'Last name', field: 'last_name' },
  { prefix: 'Email', field: 'email' },
  { prefix: 'Company name', field: 'company_name' },
];

const mapServerErrors = (errors = []) => {
  const fieldErrors = {};
  const banner = [];

  errors.forEach((err) => {
    const match = fielPrefixes.find(({ prefix }) => err.startsWith(prefix));
    if (match && !fieldErrors[match.field]) {
      fieldErrors[match.field] = err;
    } else {
      banner.push(err);
    }
  });

  return { fieldErrors, banner };
};

const PageContainer = ({ token, error, initialView, initialEmail, expiredOn, onResendClick }) => {
  const [view, setView] = useState(initialView);
  const [submittedUser, setSubmittedUser] = useState({
    first_name: '',
    email: initialEmail,
  });
  const [processing, setProcessing] = useState(false);
  const [responseError, setResponseError] = useState({});
  const [bannerError, setBannerError] = useState([]);

  const resetSubmissionState = () => {
    setResponseError({});
    setBannerError([]);
  };

  const handleSubmit = (userData) => {
    setProcessing(true);
    resetSubmissionState();

    registerPressUser({ user: userData }, token)
      .then(() => {
        setSubmittedUser({ first_name: userData.first_name, email: userData.email });
        setView('submitted');
      })
      .catch((err) => {
        const errors = err.response && err.response.data && err.response.data.errors;
        if (Array.isArray(errors)) {
          const { fieldErrors, banner } = mapServerErrors(errors);
          setResponseError(fieldErrors);
          setBannerError(banner);
        } else {
          setBannerError(['Something went wrong. Please try again.']);
        }
      })
      .finally(() => {
        setProcessing(false);
      });
  };

  const handleRenewalSubmit = (data) => {
    setProcessing(true);
    resetSubmissionState();

    requestPressRenewal({ user: data })
      .then(() => {
        setSubmittedUser({ first_name: '', email: data.email });
        setView('renewal_submitted');
      })
      .catch(() => {
        setBannerError(['Something went wrong. Please try again.']);
      })
      .finally(() => {
        setProcessing(false);
      });
  };

  if (view === 'submitted') {
    return (
      <PageLayout>
        <div className="press-register-card press-register-card--standalone">
          <StateRequestSubmitted firstName={submittedUser.first_name} email={submittedUser.email} />
        </div>
      </PageLayout>
    );
  }

  if (view === 'check_inbox') {
    return (
      <PageLayout>
        <div className="press-register-card press-register-card--standalone">
          <StateCheckInbox
            email={submittedUser.email}
            error={error}
            onResendClick={onResendClick}
          />
        </div>
      </PageLayout>
    );
  }

  if (view === 'renewal_submitted') {
    return (
      <PageLayout>
        <div className="press-register-card press-register-card--standalone">
          <StateRenewalSubmitted email={submittedUser.email} />
        </div>
      </PageLayout>
    );
  }

  if (view === 'expired') {
    return (
      <PageLayout>
        <div className="press-register-card">
          <StateExpired
            expiredOn={expiredOn}
            initialEmail={initialEmail}
            processing={processing}
            responseError={responseError}
            bannerError={bannerError}
            onSubmit={handleRenewalSubmit}
          />
        </div>
        <div className="press-register-expired__footer">
          Renewal requests are reviewed manually. You&apos;ll receive an email once your request has
          been approved or denied.
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="press-register-card">
        <h1 className="press-register-card__title">Request Press Access</h1>
        <p className="press-register-card__subtitle">
          Complete the form below. Your request will be reviewed by our PR team and you&apos;ll hear
          back by email.
        </p>
        <PressRegisterForm
          processing={processing}
          responseError={responseError}
          bannerError={bannerError}
          onSubmit={handleSubmit}
        />
      </div>
      <p className="press-register-page__signin">
        Already have access? <a href={signInUrl}>Sign in here.</a>
      </p>
    </PageLayout>
  );
};

PageContainer.propTypes = {
  token: T.string,
  initialView: T.oneOf(['form', 'submitted', 'check_inbox', 'expired', 'renewal_submitted']),
  initialEmail: T.string,
  expiredOn: T.string,
  error: T.string,
  onResendClick: T.func,
};

PageContainer.defaultProps = {
  token: '',
  initialView: 'form',
  initialEmail: '',
  expiredOn: '',
  onResendClick: () => {},
};

export default PageContainer;
